import React from 'react'
import clsx from 'clsx'
import { styled } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import {
    AppliedFiltersButton,
    classToFComponent,
    FCClassContext,
    ModuleView,
    PageSubtitle,
    PageTitle,
    ScrollablePageView,
    ToolbarDivider,
    ToolbarSectionTitleResponsive
} from 'src/utils/views'

import { AnaliseDesempenhoScope, HeaderScope } from '../ad_scopes'
import { ComparativoView } from './ad_comparativo'
import { Aviso, KpiView, LoadError } from './ad_components'
import { AdFilterPaneView } from './ad_filter_pane'
import { GraficoView } from './ad_grafico'

const PageHead = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 24,
    '@media (max-width: 720px)': { flexDirection: 'column', gap: 12 }
})

const HeadActions = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flex: '0 0 auto'
})

const KPI_ROW = 88
const MAIN_ROW = 930

const useStyles = makeStyles()({
    page: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minWidth: 0
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.65fr) minmax(0, 1fr)',
        gridTemplateRows: `${KPI_ROW}px ${MAIN_ROW}px`,
        gridTemplateAreas: `
            "kpis grafico"
            "tabela grafico"
        `,
        gap: 10,
        '@media (max-width: 1240px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
            gridTemplateRows: `${KPI_ROW}px ${MAIN_ROW}px 520px`,
            gridTemplateAreas: '"kpis" "tabela" "grafico"'
        },
        '@media (max-width: 720px)': {
            gridTemplateRows: `${KPI_ROW * 3 + 16}px ${MAIN_ROW}px 480px`
        }
    },
    kpis: {
        gridArea: 'kpis',
        display: 'grid',
        gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
        gap: 8,
        '@media (max-width: 720px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }
    },
    tabela: { gridArea: 'tabela', minHeight: 0 },
    grafico: { gridArea: 'grafico', minHeight: 0 },
    overlay: {
        flexShrink: 0,
        marginRight: 5,
        marginBottom: 5
    }
})

type AnaliseDesempenhoViewProps = {
    className?: string
    scope: AnaliseDesempenhoScope
}

class AnaliseDesempenhoViewClass implements FCClassContext<AnaliseDesempenhoViewProps> {
    scope!: AnaliseDesempenhoScope
    classes!: ReturnType<typeof useStyles>['classes']

    onSyncState({ scope }: AnaliseDesempenhoViewProps) {
        this.scope = scope
        this.classes = useStyles().classes
    }

    render({ className }: AnaliseDesempenhoViewProps) {
        const { classes, scope } = this
        return (
            <ModuleView className={className}>
                <ScrollablePageView>
                    <div className={classes.page}>
                        <PageHead>
                            <div>
                                <PageTitle>Análise de Desempenho</PageTitle>
                                <PageSubtitle>{scope.atualizacao}</PageSubtitle>
                            </div>
                            <HeadActions>
                                <AppliedFiltersButton filters={scope.appliedFilters} />
                            </HeadActions>
                        </PageHead>

                        <LoadError message={scope.erro} />
                        <Aviso message={scope.avisoLimite} />

                        <div className={classes.grid}>
                            <div className={classes.kpis}>
                                <KpiView scope={scope.operadoras} />
                                <KpiView scope={scope.beneficiarios} />
                                <KpiView scope={scope.liquidezGeral} />
                                <KpiView scope={scope.liquidezCorrente} />
                                <KpiView scope={scope.sinistralidade} />
                            </div>
                            <ComparativoView className={classes.tabela} scope={scope.comparativo} />
                            <GraficoView className={classes.grafico} scope={scope.grafico} />
                        </div>
                    </div>
                </ScrollablePageView>
                <AdFilterPaneView className={classes.overlay} scope={scope.filtros} />
            </ModuleView>
        )
    }
}

export const AnaliseDesempenhoView = classToFComponent(AnaliseDesempenhoViewClass, React)
export default AnaliseDesempenhoView

// :: Barra superior

const useHeaderStyles = makeStyles()({
    view: {
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'stretch',
        flexGrow: 1,
        gap: 12,
        marginLeft: 5,
        minWidth: 0,
        width: 0
    }
})

type HeaderViewProps = {
    className?: string
    scope: HeaderScope
}

class HeaderViewClass implements FCClassContext<HeaderViewProps> {
    render({ className }: HeaderViewProps) {
        const { classes } = useHeaderStyles()
        return (
            <div className={clsx(classes.view, className)}>
                <ToolbarDivider orientation="vertical" variant="middle" flexItem />
                <ToolbarSectionTitleResponsive>Análise de Desempenho</ToolbarSectionTitleResponsive>
            </div>
        )
    }
}

export const HeaderView = classToFComponent(HeaderViewClass, React)
